import { useQuery } from '@apollo/client/react';
import { NetworkStatus } from '@apollo/client';
import { GET_TIMELINE_TAB_DATA } from '../graphql/projectTimelineQueries';
import { TimelineItemType } from '../types';
import useProjectNotesService from '../useProjectNotesService';
import { PROJECT_NOTE_SOURCE, PROJECT_TIMELINE_RECORD_FILTER_TYPE, PROJECT_TIMELINE_TYPE } from '../constants';
import {
  PROJECT_CLOSEOUT_SOURCE,
  PROJECT_SUMMARY_REVIEW_SOURCE,
  PROJECT_SYSTEM_NOTE_SOURCE,
} from '../useNoteTimelineRegistry';
import { TimelineResponseData } from 'framework/ui/timeline';
import { useCallback } from 'react';
import { FilterCondition } from 'framework/types';

export interface ProjectTimelineResponse {
  id: string | number;
  timeline: TimelineItemType[];
}

const isSummaryReviewItem = (item: TimelineItemType) =>
  (item.payload as Record<string, unknown> | undefined)?.['__typename'] === 'SummaryReviewPayload';

const getTimelineRecordFilterType = (item: TimelineItemType) => {
  if (isSummaryReviewItem(item)) return PROJECT_TIMELINE_RECORD_FILTER_TYPE.SUMMARY_REVIEW;
  return item.type;
};

const timelineItemToEntity = (item: TimelineItemType) => {
  const isSystemNote = item.type === PROJECT_TIMELINE_TYPE.SYSTEM_NOTE;
  const isCloseout = item.type === PROJECT_TIMELINE_TYPE.CLOSEOUT;
  const isSummaryReview = isSummaryReviewItem(item);

  return {
    id: item.id,
    timelineId: item.id,
    isPinned: item.isPinned,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    createdBy: item.createdBy,
    updatedBy: item.updatedBy,
    createdById: (item as TimelineItemType & { createdById?: number }).createdById ?? 0,
    type: item.type,
    ...(isSystemNote
      ? { [PROJECT_SYSTEM_NOTE_SOURCE]: item }
      : isCloseout
        ? { [PROJECT_CLOSEOUT_SOURCE]: item }
        : isSummaryReview
          ? { [PROJECT_SUMMARY_REVIEW_SOURCE]: item }
          : { [PROJECT_NOTE_SOURCE]: item }),
  };
};

const timelineToResponse = (items: TimelineItemType[] = []): TimelineResponseData => ({
  result: items.map(timelineItemToEntity) as unknown as TimelineResponseData extends { result: infer R } ? R : never,
  pageNumber: 0,
  totalPages: 1,
  totalElements: items.length,
});

export const useProjectTimeline = (id: string | number) => {
  const { createUserNote, getAccountStrategyNote } = useProjectNotesService();

  const { data, loading, error, refetch, networkStatus } = useQuery<{ baseProject: ProjectTimelineResponse }>(
    GET_TIMELINE_TAB_DATA,
    {
      variables: { id: id.toString() },
      skip: !id,
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  );

  const getTimelineItems = useCallback(
    async (_entityId: string, request: { filter?: { type?: { [key: string]: string[] } } }) => {
      const typeFilter: string[] | undefined = request?.filter?.type?.[FilterCondition.IN];
      const allItems: TimelineItemType[] = data?.baseProject?.timeline ?? [];
      const filtered = typeFilter?.length
        ? allItems.filter(item => typeFilter.includes(getTimelineRecordFilterType(item)))
        : allItems;
      const strategyNotes = (await getAccountStrategyNote(id.toString())) ?? [];
      const combinedItems = [...strategyNotes, ...filtered];
      return Promise.resolve(timelineToResponse(combinedItems));
    },
    [data?.baseProject?.timeline, getAccountStrategyNote, id],
  );

  const getTimelinePageById = useCallback(
    (_entityId: string, _entryId: string) => {
      const allItems: TimelineItemType[] = data?.baseProject?.timeline ?? [];
      return Promise.resolve(timelineToResponse(allItems));
    },
    [data?.baseProject?.timeline],
  );

  const createTimelineItem = useCallback(
    async (_projectId: string, _itemType: string, request: { text: string; attachmentPaths?: string[] }) => {
      const created = await createUserNote(_projectId, {
        text: request.text,
        attachmentPaths: request.attachmentPaths ?? [],
      });
      return { ...created, timelineId: created.id };
    },
    [createUserNote],
  );

  return {
    data: data?.baseProject,
    loading,
    isDataReloading: networkStatus !== NetworkStatus.ready,
    error,
    refetch,
    getTimelineItems,
    getTimelinePageById,
    createTimelineItem,
  };
};
